import { LoadingButton } from "@mui/lab";
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { LocationPicker } from "components/lib-ui/LocationPicker";
import { RELOCATION_OPTIONS } from "constants/relocation";
import { mapCandidateSelectedOption, relocationOptionIdToData, removeEmptyLocations } from "helpers/relocation";
import { useUpdateCandidateById } from "hooks/useUpdateCandidateById";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";



export const RelocationForm = ({ setIsDialogOpen, options }) => {
    const candidateId = useSelector((state) => state.currentCandidate.id);

    const { lookingFor } = useSelector(
        (state) => state.currentCandidate.personalDetailsAndPreferences,
    );

    const candidateRelocation = lookingFor?.relocation || null;

    const { updateCandidateById, requestSuccessful, isLoading } =
        useUpdateCandidateById();

    const [includedLocations, setIncludedLocations] = useState(
        candidateRelocation?.included || [],
    );

    const [excludedLocations, setExcludedLocations] = useState(
        candidateRelocation?.excluded || [],
    );

    const selectedRelocationOption = options.find(
        (option) =>
            option.id === mapCandidateSelectedOption(candidateRelocation),
    );

    const [value, setValue] = useState(selectedRelocationOption || null);

    const [showPickers, setShowPickers] = useState(false);

    const handleAddLocation = (type) => {
        if (type === "included") {
            setIncludedLocations((oldLocations) => [...oldLocations, {}]);
        } else if (type === "excluded") {
            setExcludedLocations((oldLocations) => [...oldLocations, {}]);
        }
    };

    const handleEditLocation = (type, index, location) => {
        if (type === "included") {
            const updatedLocations = [...includedLocations];
            updatedLocations[index] = { ...location };
            setIncludedLocations(updatedLocations);
        } else if (type === "excluded") {
            const updatedLocations = [...excludedLocations];
            updatedLocations[index] = { ...location };
            setExcludedLocations(updatedLocations);
        }
    };

    const handleRemoveLocation = (type, index) => {
        if (type === "included") {
            const updatedLocations = [...includedLocations];
            updatedLocations.splice(index, 1);
            setIncludedLocations(updatedLocations);
        } else if (type === "excluded") {
            const updatedLocations = [...excludedLocations];
            updatedLocations.splice(index, 1);
            setExcludedLocations(updatedLocations);
        }
    };

    const showRelocationPickers = useCallback((id) => {
        if (id === RELOCATION_OPTIONS.SPECIFIC_LOCATIONS) {
            setShowPickers(true);
        } else {
            setShowPickers(false);
        }
    }, []);

    const submitForm = () => {
        let relocationData;
        if (value === null) relocationData = null;
        else {
            relocationData = {
                included: removeEmptyLocations(includedLocations),
                excluded: removeEmptyLocations(excludedLocations),
                ...relocationOptionIdToData(value.id),
            };
        }
        updateCandidateById(candidateId, { relocation: relocationData });
    };

    useEffect(() => {
        if (requestSuccessful) {
            setIsDialogOpen(false);
        }
    }, [requestSuccessful, setIsDialogOpen]);

    useEffect(() => {
        if (value === null) {
            setShowPickers(false);
        } else {
            showRelocationPickers(value.id);
        }
    }, [value, showRelocationPickers]);

    const { handleSubmit } = useForm();

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(submitForm)}
            sx={{ mt: 2, minHeight: "350px" }}
        >
            <Stack gap={4}>
                <Autocomplete
                    fullWidth
                    autoComplete
                    defaultValue={null}
                    options={options || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    value={value || null}
                    onChange={(_, newValue) => setValue(newValue)}
                    renderInput={(params) => (
                        <TextField
                            variant="standard"
                            {...params}
                            label="Relocation Option"
                        />
                    )}
                />
                {showPickers && (
                    <>
                        <Box>
                            <Box mb={2}>
                                <Typography variant="header2">
                                    Included Locations
                                </Typography>
                            </Box>
                            <Stack gap={1}>
                                {includedLocations.map((location, index) => (
                                    <Box key={Date.now() + index}>
                                        <LocationPicker
                                            value={location}
                                            type={"included"}
                                            index={index}
                                            handlePickLocation={
                                                handleEditLocation
                                            }
                                            label={`Location ${index + 1}`}
                                            handleRemoveLocation={() =>
                                                handleRemoveLocation(
                                                    "included",
                                                    index,
                                                )
                                            }
                                        />
                                    </Box>
                                ))}
                            </Stack>
                            <Button
                                sx={{
                                    borderRadius: "50px",
                                    padding: "10px 30px",
                                    mt: 2,
                                }}
                                size="small"
                                color="primary"
                                variant="contained"
                                onClick={() => handleAddLocation("included")}
                            >
                                + Included Location
                            </Button>
                        </Box>
                        <Box>
                            <Box mb={2}>
                                <Typography variant="header2">
                                    Excluded Locations
                                </Typography>
                            </Box>
                            <Stack gap={1}>
                                {excludedLocations.map((location, index) => (
                                    <Box key={Date.now() + index}>
                                        <LocationPicker
                                            value={location}
                                            type={"excluded"}
                                            index={index}
                                            handlePickLocation={
                                                handleEditLocation
                                            }
                                            label={`Location ${index + 1}`}
                                            handleRemoveLocation={() =>
                                                handleRemoveLocation(
                                                    "excluded",
                                                    index,
                                                )
                                            }
                                        />
                                    </Box>
                                ))}
                            </Stack>
                            <Button
                                sx={{
                                    borderRadius: "50px",
                                    padding: "10px 30px",
                                    mt: 2,
                                }}
                                size="small"
                                color="primary"
                                variant="contained"
                                onClick={() => handleAddLocation("excluded")}
                            >
                                + Excluded Location
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                    sx={{
                        borderRadius: "50px",
                        padding: "10px 30px",
                    }}
                    size="small"
                    color="primary"
                    type="submit"
                    loadingPosition="start"
                    loading={isLoading}
                >
                    Save changes
                </LoadingButton>
            </Stack>
        </Box>
    );
};
